// src/store/alertStore.js
// Alert data: live NWS alerts, USGS gauge readings.
// Alert preferences: notification channels, alert type subscriptions.
// Two stores — one is server-side data (never persisted),
// one is user config (persisted to localStorage).

import { create } from 'zustand';
import { setSevereWeatherFlag } from '../utils/severeWeatherFlag';
import { persist, createJSONStorage } from 'zustand/middleware';
import { fetchNWSAlerts, fetchUSGSGauges } from '@services/alerts';

// ── useAlertStore — live server data ─────────────────────────────
// Not persisted: always fresh-fetched on load via useAlerts hook.
export const useAlertStore = create((set, get) => ({
  nwsAlerts:   [],
  alertsVersion: 0,
  expiryVersion:  0,
  usgsGauges:  [],
  loading:     false,
  lastUpdated: null,
  error:       null,

  setAlerts: (alerts) => {
    // Update severe weather flag for next-session disclaimer gate
    const hasSevere = (alerts ?? []).some(a =>
      a.severity === 'Extreme' || a.severity === 'Severe'
    );
    setSevereWeatherFlag(hasSevere);
    return set(s => ({ nwsAlerts: alerts, lastUpdated: Date.now(), error: null, alertsVersion: s.alertsVersion + 1 }));
  },
  tickExpiry: () => set(s => ({ expiryVersion: s.expiryVersion + 1 })),
  setGauges: (gauges) => set({ usgsGauges: gauges }),
  setError:  (error)  => set({ error, loading: false }),

  fetchAlerts: async () => {
    set({ loading: true, error: null });
    try {
      const alerts = await fetchNWSAlerts();
      set({ nwsAlerts: alerts, loading: false, lastUpdated: Date.now() });
      return alerts;
    } catch (err) {
      set({ loading: false, error: err.message });
      return [];
    }
  },

  fetchGauges: async () => {
    try {
      const gauges = await fetchUSGSGauges();
      set({ usgsGauges: gauges });
      return gauges;
    } catch { return []; }
  },

  refreshAll: async () => {
    const [alerts, gauges] = await Promise.allSettled([
      get().fetchAlerts(),
      get().fetchGauges(),
    ]);
    return {
      alerts: alerts.status === 'fulfilled' ? alerts.value : [],
      gauges: gauges.status === 'fulfilled' ? gauges.value : [],
    };
  },
}));

// ── useAlertPrefsStore — user notification preferences ────────────
// Persisted: user configures once, survives app restarts.
export const useAlertPrefsStore = create(
  persist(
    (set) => ({
      // Delivery channels
      channels: {
        push:    true,
        sms:     true,
        vibrate: true,
        sound:   false,
        flash:   false,
        email:   false,
      },
      // Alert types to receive
      types: {
        nws_extreme: true,
        nws_severe:  true,
        sos_nearby:  true,
        evacuation:  true,
        power:       false,
        road:        false,
        shelter:     true,
        curfew:      true,
      },
      // Contact info for SMS/email fallback
      phone: '',
      phoneVerified: false,
      email: '',

      setPhoneVerified: (v)    => set({ phoneVerified: v }),
      setChannel: (id, val) => set((s) => ({ channels: { ...s.channels, [id]: val } })),
      setType:    (id, val) => set((s) => ({ types:    { ...s.types,    [id]: val } })),
      setPhone:   (phone)   => set({ phone }),
      setEmail:   (email)   => set({ email }),
    }),
    {
      name:    'na-alert-prefs',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
